import type { Query } from "@tanstack/react-query";
import { myWorkFeedPrefix } from "@/queries/my-work";
import { rereadOlderThan } from "@/queries/reread";
import { scopedKey, type QueryScope } from "@/queries/scope";
import { timelinePrefix } from "@/queries/timeline";
import { isDetailOf, WORK_ITEMS_DOMAIN } from "@/queries/work-items";
import { observedRead, type THintTarget, type THintTargets } from "./types";

/** Every work-item list, every family: projects, archives, workspace, sub-items. */
const lists = observedRead((scope) => scopedKey(scope, WORK_ITEMS_DOMAIN));
/** The timeline's windows, which lay out work items by their dates. */
const timeline = observedRead(timelinePrefix);
/** My Work's feed, every sort, and the hours it shows booked. */
const myWorkFeed = observedRead(myWorkFeedPrefix);
const myWorkAvailability = observedRead((scope) => scopedKey(scope, "my-work", "availability"));

/**
 * The reads one work item's page and side panel hold, for the records `ids`
 * names (every record when it names none). Spelled out by key, since the hint
 * names records, not keys: the full record (`'work-item'`, by its id or its
 * identifier), an event's page (`'event'`), the comments, activity, links,
 * relations, attachments and logged time, and the sub-items its page lists.
 */
function recordReads(scope: QueryScope, ids: ReadonlySet<string> | undefined) {
  const named = (id: unknown) => ids === undefined || ids.has(String(id));
  const isNamedRecord = ids ? isDetailOf(scope.tenant, ids) : () => true;
  return (query: Query): boolean => {
    const [tenant, principal, domain, first, second] = query.queryKey;
    if (tenant !== scope.tenant || principal !== scope.principal) return false;
    switch (domain) {
      case "work-item":
        return isNamedRecord(query);
      case "event":
      case "work-item-activity":
      case "work-item-comments":
      case "work-item-links":
      case "work-item-attachments":
        return named(first);
      case "work-item-relations":
        return first === "item" && named(second);
      case "worklogs":
        return (first === "work-item" || first === "total") && named(second);
      case WORK_ITEMS_DOMAIN:
        return first === "sub-issues" && named(second);
      default:
        return false;
    }
  };
}

/** When the latest `work-items:item` hint arrived, per workspace and principal. */
const itemHintedAt = new Map<string, number>();
const scopeId = (scope: QueryScope) => `${scope.tenant}|${scope.principal}`;

/**
 * `work-items:item`: the records changed, so their page and panels are marked
 * stale and the ones on screen re-read. An editor keeps its own draft across
 * the re-read: a description takes up the re-read text only while it holds no
 * unsaved text and has no focus (`useDescriptionDraft`), and a title seeds its
 * draft when editing starts.
 *
 * The re-read asks again for every answer read before the latest hint, and
 * cancels nothing (`rereadOlderThan`). A plain `refetchQueries({ stale: true })`
 * lost a second edit: a re-read the first hint started, still in flight when the
 * second hint marked the record stale, landed after it and cleared the mark, so
 * the second re-read found nothing stale and the page kept the first edit for
 * the rest of its minute (NG-3702). The latest hint's time is the safe stamp
 * for a burst: it can only re-read more.
 *
 * Not re-read for this tab's own write (NG-3587): every writer on the page and
 * panel refreshes what it changed (the record and list rows through
 * `writeWorkItemRows`, the history through `markWorkItemActivityStale`, and
 * comments, links, relations, attachments, logged time and sub-items
 * through their own hooks), so the echo would only re-render under someone
 * still typing.
 */
const records: THintTarget = {
  mark: (_root, _slug, { client, scope }, ids) => {
    itemHintedAt.set(scopeId(scope), Date.now());
    void client.invalidateQueries({ predicate: recordReads(scope, ids), refetchType: "none" });
  },
  reread: (_root, _slug, { client, scope }, ids) => {
    const at = itemHintedAt.get(scopeId(scope));
    if (at === undefined) return;
    void rereadOlderThan(client, recordReads(scope, ids), at);
  },
  skipOwnReread: true,
};

/**
 * The work-items domain's hint rows (NG-3588). A task, event or milestone write
 * sends both: `list` for the lists that show it, `item` naming the records.
 * A write past the backend's 200-record cap, or one that changed every record
 * at once (a workspace re-date), sends `list` alone, and an open record shows
 * it on its next read: a focus or a revisit once its minute's reuse is up.
 */
export const WORK_ITEMS_HINTS: THintTargets = {
  // Every list, the timeline and My Work: which rows a filtered list holds, and
  // their values, are the server's answer. Re-read for this tab's own write
  // too: a field edit patches its row in place, and only a read says whether
  // the row still meets a list's filter.
  "work-items:list": {
    mark: (root, slug, cache) => {
      for (const read of [lists, timeline, myWorkFeed, myWorkAvailability]) {
        read.mark(root, slug, cache);
      }
    },
    reread: (root, slug, cache) => {
      for (const read of [lists, timeline, myWorkFeed, myWorkAvailability]) {
        read.reread(root, slug, cache);
      }
    },
  },
  "work-items:item": records,
};
